import { ErrorMessage, Form, Formik } from 'formik';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import PhoneInputWithCountrySelect from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Link, useNavigate } from 'react-router-dom';
import Select, { MultiValue } from 'react-select';
import countries from 'world-countries';
import * as Yup from 'yup';

import AuthInput from '@/components/auth/AuthInput';
import { ProfileCancelIcon } from '@/components/ui/svg/ProfileIcons';
import { productInterestOptions } from '@/components/utils/constants';
import { useUser } from '@/hooks/useUser';
import { ConsumerRoutes } from '@/interfaces/routes/ConsumerRoutes';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const AddProfile = () => {
	const { user, refetch } = useUser();
	const navigate = useNavigate();
	const countryOptions = countries.map((c) => ({
		value: c.name,
		label: `${c.flag} ${c.name.common}`,
	}));

	const handleProductInterestChange = (
		e: MultiValue<{ value: string; label: string }>,
		setFieldValue: (field: string, value: any) => void,
	) => {
		setFieldValue('productInterest', e);
	};

	return (
		<div className="max-w-4xl mx-auto bg-white shadow-md rounded-lg p-4 sm:p-6 lg:p-8">
			<div className="flex justify-between items-start sm:items-center border-b pb-3 sm:pb-4 mb-4 sm:mb-6">
				<div className="flex-1 min-w-0">
					<h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 truncate">
						Profile
					</h2>
					<p className="text-gray-400 text-xs sm:text-sm mt-1 truncate">
						{user?.username || ''}
					</p>
				</div>
				<Link to={ConsumerRoutes.Profile} className="ml-4 flex-shrink-0">
					<button className="text-gray-500 hover:text-gray-700 p-1">
						<ProfileCancelIcon />
					</button>
				</Link>
			</div>

			<Formik
				initialValues={{
					name: user?.name || '',
					email: user?.username || '',
					phone: user?.phone || '',
					location: user?.location || '',
					shippingAddress: user?.shippingAddress || '',
					productInterest: user?.productInterest
						? user.productInterest
								.split(',')
								.map(
									(label) =>
										productInterestOptions.find(
											(option) => option.label === label,
										) || { label: label, value: label },
								)
						: [],
				}}
				validationSchema={Yup.object({
					name: Yup.string().required('Required'),
					email: Yup.string().email('Invalid email').required('Required'),
					phone: Yup.string()
						.test('valid-phone', 'Invalid phone number format', (value) => {
							if (!value) return true;
							return isValidPhoneNumber(value);
						})
						.test('valid-length', 'Invalid phone number length', (value) => {
							if (!value) return true;
							try {
								const parsedNumber = parsePhoneNumber(value);
								return parsedNumber && parsedNumber.isValid();
							} catch (error) {
								return false;
							}
						}),
					location: Yup.string(),
					shippingAddress: Yup.string(),
					productInterest: Yup.array().of(
						Yup.object().shape({
							value: Yup.string(),
							label: Yup.string(),
						}),
					),
				})}
				onSubmit={async (values) => {
					const sanitizedValues = Object.fromEntries(
						Object.entries(values).map(([key, value]) => [
							key,
							value === '' ? null : value,
						]),
					);
					const payload = {
						...sanitizedValues,
						productInterest: values.productInterest
							.map((item) => item.label)
							.join(','),
					};
					haggleApiService
						.updateUserProfile(payload)
						.then(() => {
							notificationService.success('Profile updated successfully');
							refetch();
							navigate(ConsumerRoutes.Profile);
						})
						.catch(() => {
							notificationService.error('Error updating profile');
						});
				}}
			>
				{({ errors, touched, values, setFieldValue }) => (
					<Form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
						<div className="space-y-4 md:space-y-0 md:contents">
							<AuthInput
								name="name"
								label="Full Name"
								type="text"
								length={24}
								error={!!errors.name}
								touched={touched.name}
							/>
							<AuthInput
								name="email"
								label="Email Address"
								type="email"
								error={!!errors.email}
								touched={touched.email}
								disabled={true}
							/>
						</div>

						<div className="space-y-4 md:space-y-0 md:contents">
							<div className="w-full">
								<label className="block mb-2 text-sm sm:text-base leading-5 font-[450]">
									Phone Number
								</label>
								<PhoneInputWithCountrySelect
									international
									defaultCountry="AU"
									value={user?.phone || ''}
									onChange={(value) => setFieldValue('phone', value)}
									className="block w-full h-11 sm:h-12 px-3 sm:px-4 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-0 focus:shadow-borders-interactive-with-active text-sm sm:text-base"
									style={{
										backgroundColor: 'white',
										color: 'black',
										border: '1px solid #E5E7EB',
									}}
								/>
								<ErrorMessage
									name="phone"
									component="p"
									className="text-red-500 text-xs sm:text-sm w-full mt-1"
								/>
							</div>

							<div className="w-full">
								<label className="block mb-2 text-sm sm:text-base leading-5 font-[450]">
									Country of Residence
								</label>
								<Select
									name="location"
									placeholder="Select Country"
									options={countryOptions}
									className="basic-multi-select text-sm sm:text-base"
									classNamePrefix="select"
									value={countryOptions.find(
										(option) => option.value.common === values.location,
									)}
									onChange={(country) =>
										setFieldValue('location', country?.value.common)
									}
									styles={{
										control: (provided) => ({
											...provided,
											minHeight: '2.75rem',
											border: '1px solid #E5E7EB',
										}),
									}}
								/>
							</div>
						</div>

						<div className="md:col-span-2 space-y-4">
							<AuthInput
								name="shippingAddress"
								label="Shipping Address"
								type="text"
								length={200}
								error={!!errors.shippingAddress}
								touched={touched.shippingAddress}
							/>

							<div className="flex flex-col w-full relative">
								<label className="block mb-2 text-sm sm:text-base leading-5 font-[450]">
									Product Interest
								</label>
								<Select
									id="productInterest"
									options={productInterestOptions}
									isMulti
									className="basic-multi-select text-sm sm:text-base"
									placeholder="Select Product Interests"
									classNamePrefix="select"
									name="productInterest"
									value={values.productInterest}
									onChange={(e) =>
										handleProductInterestChange(e, setFieldValue)
									}
									styles={{
										control: (provided) => ({
											...provided,
											minHeight: '2.75rem',
											border: '1px solid #E5E7EB',
										}),
									}}
								/>
							</div>
						</div>

						<div className="md:col-span-2 flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 sm:mt-8">
							<button
								type="submit"
								className="flex-1 sm:flex-none px-5 py-3 text-center text-sm sm:text-base font-medium bg-purpleBrand-500 hover:bg-purpleBrand-600 rounded-md active:bg-purpleBrand-900 text-white transition-colors duration-200"
							>
								Save Changes
							</button>
							<Link to={ConsumerRoutes.Profile} className="flex-1 sm:flex-none">
								<button
									type="button"
									className="w-full border px-5 py-3 text-center text-sm sm:text-base font-medium rounded-md hover:bg-gray-100 transition-colors duration-200"
								>
									Cancel
								</button>
							</Link>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default AddProfile;
